/**
 * Event Builder — provider (owns the reducer, the save engine, the loaders) and `useBuilder()`.
 *
 * Plan references: 3.3 (state), 3.4 (engine), 9.1 (load sequence), 9.2 (defaults), D7 (`?section=`).
 */

import React, { createContext, useCallback, useContext, useEffect, useMemo, useReducer, useRef } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useLocation, useNavigate } from "react-router-dom";
import qs from "query-string";

import { fetchUserFeatures } from "../../../../actions/featureAccessActions";
import { getUserAndPlanDetails } from "../../../../actions/subscriptionActions";
import { FEATURE_IDS } from "../../../../constants/featureAccess";
import { getAdminBusinessId } from "../../../../utils/authHelper";
import { canUseOrganizationStaff } from "../../../../utils/organizationStaffAccess";
import { isSmsAllowed } from "../../../../utils/trialAccess";
import { notification } from "../../../../utils/utility";

import { actions, builderReducer, initialBuilderState } from "./builderReducer";
import {
  CONFERENCING_VENUES,
  DEFAULT_WORK_DAYS,
  DEFAULT_WORK_END,
  DEFAULT_WORK_START,
  E,
  INVITEE_CALL_STATUS,
  KIND,
  ROUTES,
  SCHEDULE_SOURCE,
  SCHEDULE_TYPE,
  SECTIONS,
  STEPS,
  VENUE,
  isValidSection,
  kindFromQuery,
  queryFromKind,
} from "./constants";
import * as api from "./api";
import {
  defaultCompanyTemplate,
  filterActiveStaff,
  hydrateAdvanced,
  hydrateAvailability,
  hydrateDetails,
  hydrateType,
  templateToRows,
} from "./hydrate";
import { createSaveEngine, validateStep } from "./saveEngine";
import { featureEnabled, flattenIssues, isVenueConnected } from "./validation";

const BuilderContext = createContext(null);

/**
 * Insurance for the `/builder` → `/builder/:id` URL rewrite after the first type save (3.4 rule 1):
 * react-router 6 keeps the component mounted (same element type at the same depth), but if the
 * router ever remounts us we pick the live state back up instead of re-loading a half-saved draft.
 */
let handoff = null;
const takeHandoff = (eventId) => {
  if (handoff && String(handoff.eventId) === String(eventId) && Date.now() - handoff.at < 10000) {
    const s = handoff.state;
    handoff = null;
    return s;
  }
  handoff = null;
  return null;
};

export const defaultWeeklyRows = () =>
  DEFAULT_WORK_DAYS.map((dayId) => ({ dayId, dated: null, schedules: [{ start_time: DEFAULT_WORK_START, end_time: DEFAULT_WORK_END }] }));

const readConfig = () => {
  try {
    return localStorage.config ? JSON.parse(localStorage.config) : {};
  } catch (e) {
    return {};
  }
};

/** Business name for the current admin business (jwt claims: `businesses: { name: id }`). */
export const currentBusinessName = () => {
  const cfg = readConfig();
  const businesses = cfg.businesses || {};
  const id = String(getAdminBusinessId() || "");
  const hit = Object.keys(businesses).find((name) => String(businesses[name]) === id);
  return hit || Object.keys(businesses)[0] || "";
};

/**
 * Default timezone for a new event: tries, in order:
 * 1. `localStorage.userTimezoneId` — updated at login and on profile save
 * 2. JWT claims `timezone_id` stored in `localStorage.config`
 * 3. Browser IANA timezone matched against the `/timezones` list
 * Returns '' when nothing matches.
 */
export const guessTimezoneId = (timezones) => {
  const list = timezones || [];
  const matches = (id) => id && list.some((t) => String(t.value) === String(id));

  const stored = localStorage.getItem("userTimezoneId") || "";
  if (matches(stored)) return String(stored);

  const cfg = readConfig();
  if (matches(cfg.timezone_id)) return String(cfg.timezone_id);

  let zone = "";
  try {
    zone = Intl.DateTimeFormat().resolvedOptions().timeZone || "";
  } catch (e) {
    zone = "";
  }
  if (!zone) return "";
  const hit = list.find(
    (t) => String(t.timezone_value || "") === zone || String(t.label || "").includes(zone)
  );
  return hit ? String(hit.value) : "";
};

export function BuilderProvider({ eventId: routeEventId, children }) {
  const reduxDispatch = useDispatch();
  const navigate = useNavigate();
  const location = useLocation();
  const query = useMemo(() => qs.parse(location.search || ""), [location.search]);

  const featureAccess = useSelector((s) => s.featureAccess || {});
  const userPlan = useSelector((s) => (s.subscription || {}).userPlan);
  const features = useMemo(() => featureAccess.features || [], [featureAccess.features]);
  const limits = useMemo(() => featureAccess.limits || {}, [featureAccess.limits]);
  const featuresLoaded = Boolean(featureAccess.loaded);
  // 3.4 rule 10: nothing gate-dependent is computed or saved before both are loaded. The
  // subscription reducer starts with `plan_id: ''`, so a truthy plan_id means the plan arrived
  // (the old page's `!this.props.userPlan` check is always false against that initial object).
  const ready = featuresLoaded && Boolean(userPlan && userPlan.plan_id);

  const recoveredRef = useRef(false);
  const startedRef = useRef(false); // loaders started (a recovered state never re-loads)
  const [state, dispatch] = useReducer(
    builderReducer,
    { routeEventId, query },
    ({ routeEventId: id, query: q }) => {
      const recovered = id ? takeHandoff(id) : null;
      if (recovered) {
        recoveredRef.current = true;
        startedRef.current = true;
        return recovered;
      }
      return initialBuilderState({
        eventId: id,
        kind: kindFromQuery(q.type),
        section: isValidSection(q.section) ? q.section : SECTIONS.QUICK,
      });
    }
  );

  // latest values for the engine / async loaders
  const stateRef = useRef(state);
  stateRef.current = state;
  const reduxRef = useRef({});
  reduxRef.current = {
    userPlan,
    features,
    limits,
    featuresLoaded: ready,
    featureAccess,
    smsAllowed: isSmsAllowed(userPlan, featureAccess),
  };

  const businessId = getAdminBusinessId();

  // ---- plan usage refresh (3.4 rule 12) ---------------------------------------------------------------
  const refreshPlanUsage = useCallback(() => {
    reduxDispatch(fetchUserFeatures());
    reduxDispatch(getUserAndPlanDetails({}));
  }, [reduxDispatch]);

  // ---- engine ---------------------------------------------------------------------------------------------
  const engineRef = useRef(null);
  if (!engineRef.current) {
    engineRef.current = createSaveEngine({
      getState: () => stateRef.current,
      dispatch,
      getRedux: () => reduxRef.current,
      onFirstSave: (id) => {
        // stateRef lags the dispatches made a moment ago (saveSuccess/setMeta) → apply them here
        const s = stateRef.current;
        handoff = {
          eventId: id,
          state: {
            ...s,
            meta: { ...s.meta, eventId: id, isNew: false },
            save: {
              ...s.save,
              savedVersion: { ...s.save.savedVersion, [STEPS.TYPE]: s.save.version[STEPS.TYPE] },
              fieldErrors: { ...s.save.fieldErrors, [STEPS.TYPE]: {} },
            },
          },
          at: Date.now(),
        };
        navigate(`${ROUTES.BUILDER}/${id}?type=${queryFromKind(s.meta.kind)}&section=${s.meta.section}`, { replace: true });
        refreshPlanUsage();
      },
      onPublished: () => refreshPlanUsage(),
    });
  }
  useEffect(() => {
    const engine = engineRef.current;
    return () => engine.destroy();
  }, []);

  // when redux becomes ready anything queued can go out; a recovered (handoff) state may hold
  // dirty steps whose queue died with the previous engine → flush them
  useEffect(() => {
    if (!ready) return;
    if (recoveredRef.current) {
      recoveredRef.current = false;
      engineRef.current.flush();
      return;
    }
    engineRef.current.pump();
  }, [ready]);

  // ---- unsaved-changes guard (3.4 rule 8) ------------------------------------------------------------
  useEffect(() => {
    const handler = (e) => {
      const s = stateRef.current;
      if (s.save.status === "saving" || engineRef.current.isBusy()) {
        e.preventDefault();
        e.returnValue = "";
      }
    };
    window.addEventListener("beforeunload", handler);
    return () => window.removeEventListener("beforeunload", handler);
  }, []);

  // ---- section ↔ URL (D7) ------------------------------------------------------------------------------------
  useEffect(() => {
    const section = isValidSection(query.section) ? query.section : SECTIONS.QUICK;
    if (section !== stateRef.current.meta.section) dispatch(actions.setMeta({ section }));
  }, [query.section]);

  const goToSection = useCallback(
    (section, { replace = false } = {}) => {
      const s = stateRef.current;
      const base = s.meta.eventId ? `${ROUTES.BUILDER}/${s.meta.eventId}` : ROUTES.BUILDER;
      navigate(`${base}?type=${queryFromKind(s.meta.kind)}&section=${section}`, { replace });
      // leaving the success screen through the nav returns to edit mode (6.I)
      dispatch(actions.setMeta({ section, published: false }));
      dispatch(actions.ui({ navOpen: false }));
      try {
        window.scrollTo({ top: 0, behavior: "auto" });
      } catch (e) {
        /* noop */
      }
    },
    [navigate]
  );

  // ---- edits ------------------------------------------------------------------------------------------------------
  const update = useCallback((step, patch) => {
    dispatch(actions.update(step, patch));
    engineRef.current.noteChange(step);
  }, []);

  /** Kind switch (6.C). Gates are checked by the caller (FeatureLock / payment modal / Q4 confirm). */
  const setKind = useCallback((kind) => {
    if (kind === stateRef.current.meta.kind) return;
    dispatch(actions.setKind(kind));
    engineRef.current.noteChange(STEPS.TYPE);
    engineRef.current.noteChange(STEPS.DETAILS);
    const s = stateRef.current;
    const base = s.meta.eventId ? `${ROUTES.BUILDER}/${s.meta.eventId}` : ROUTES.BUILDER;
    navigate(`${base}?type=${queryFromKind(kind)}&section=${s.meta.section}`, { replace: true });
  }, [navigate]);

  /** `lookups.paymentConnected`, fetching once when unknown (3.4 rule 10 — kind switch to Paid). */
  const ensurePaymentStatus = useCallback(async () => {
    const known = stateRef.current.lookups.paymentConnected;
    if (typeof known === "boolean") return known;
    try {
      const connected = await api.getPaymentIntegrationStatus();
      dispatch(actions.setLookups({ paymentConnected: connected }));
      return connected;
    } catch (e) {
      dispatch(actions.setLookups({ paymentConnected: false }));
      return false;
    }
  }, []);

  // ---- UI helpers -----------------------------------------------------------------------------------------------
  const toastTimer = useRef(null);
  const toast = useCallback((text) => {
    dispatch(actions.ui({ toast: { id: Date.now(), text } }));
    if (toastTimer.current) clearTimeout(toastTimer.current);
    toastTimer.current = setTimeout(() => dispatch(actions.ui({ toast: null })), 2500);
  }, []);
  useEffect(() => () => toastTimer.current && clearTimeout(toastTimer.current), []);

  const confirm = useCallback((cfg) => dispatch(actions.ui({ confirm: cfg })), []);
  const closeConfirm = useCallback(() => dispatch(actions.ui({ confirm: null })), []);

  const highlightTimer = useRef(null);
  const highlight = useCallback((blockId) => {
    dispatch(actions.ui({ highlight: blockId }));
    if (highlightTimer.current) clearTimeout(highlightTimer.current);
    highlightTimer.current = setTimeout(() => dispatch(actions.ui({ highlight: null })), 1200);
  }, []);

  /** Scroll to a block (Edit links, footer "n required items left"). Switches section first if needed. */
  const focusBlock = useCallback(
    (anchor, section) => {
      if (section && section !== stateRef.current.meta.section) goToSection(section);
      const run = (attempt) => {
        const el = document.getElementById(anchor);
        if (!el) {
          if (attempt < 10) setTimeout(() => run(attempt + 1), 60);
          return;
        }
        el.scrollIntoView({ block: "center", behavior: "smooth" });
        const control = el.querySelector("input, select, textarea, button");
        if (control && typeof control.focus === "function") control.focus({ preventScroll: true });
        highlight(anchor);
      };
      run(0);
    },
    [goToSection, highlight]
  );

  // ---- loaders (9.1) -------------------------------------------------------------------------------------------------
  useEffect(() => {
    // exactly as add-new-event-availbility.jsx componentDidMount (a recovered state just did this)
    if (startedRef.current) return;
    reduxDispatch(fetchUserFeatures());
    reduxDispatch(getUserAndPlanDetails({}));
  }, [reduxDispatch]);

  useEffect(() => {
    if (!ready || startedRef.current) return;
    startedRef.current = true;
    let cancelled = false;
    const safe = (fn) => (...args) => {
      if (!cancelled) fn(...args);
    };
    const redux = reduxRef.current;
    const canUseStaff = canUseOrganizationStaff(redux.userPlan);
    const s0 = stateRef.current;
    const isEdit = Boolean(s0.meta.eventId);

    // -- lookups (progressive) --
    api.getVenueList().then(safe((venues) => dispatch(actions.setLookups({ venues })))).catch(() => {});
    const timezonesP = api
      .getTimezoneList()
      .then(safe((timezones) => {
        dispatch(actions.setLookups({ timezones }));
        return timezones;
      }))
      .catch(() => []);
    const connectedVenuesP = api
      .getConnectedVenues()
      .then(safe((connectedVenues) => {
        const paymentConnected = (connectedVenues || []).some(
          (v) => [10, 11].includes(parseInt(v.app_id, 10)) && (v.connection_status === undefined || v.connection_status === E)
        );
        dispatch(actions.setLookups({ connectedVenues, connectedVenuesLoaded: true, paymentConnected }));
        return connectedVenues;
      }))
      .catch(safe(() => {
        dispatch(actions.setLookups({ connectedVenuesLoaded: true }));
        return [];
      }));
    if (featureEnabled(redux.features, FEATURE_IDS.CALENDAR_INTEGRATIONS)) {
      api.getConnectedCalendars().then(safe((connectedCalendars) => dispatch(actions.setLookups({ connectedCalendars })))).catch(() => {});
    }
    const templatesP = api
      .getScheduleTemplateList()
      .then(safe((scheduleTemplates) => {
        dispatch(actions.setLookups({ scheduleTemplates, scheduleTemplatesLoaded: true }));
        return scheduleTemplates;
      }))
      .catch(safe(() => {
        dispatch(actions.setLookups({ scheduleTemplatesLoaded: true }));
        return [];
      }));
    if (canUseStaff) {
      api
        .getStaffList()
        .then(safe((list) => dispatch(actions.setLookups({ staff: filterActiveStaff(list), staffLoaded: true }))))
        .catch(safe(() => dispatch(actions.setLookups({ staffLoaded: true }))));
    } else {
      dispatch(actions.setLookups({ staffLoaded: true }));
    }
    const configP = api
      .getEventConfig()
      .then(safe((eventConfig) => {
        dispatch(actions.setLookups({ eventConfig }));
        return eventConfig;
      }))
      .catch(() => null);
    if (s0.meta.kind === KIND.PAID && featureEnabled(redux.features, FEATURE_IDS.COLLECT_PAYMENTS)) {
      api
        .getPaymentIntegrationStatus()
        .then(safe((paymentConnected) => dispatch(actions.setLookups({ paymentConnected, paymentStatusLoaded: true }))))
        .catch(safe(() => dispatch(actions.setLookups({ paymentConnected: false, paymentStatusLoaded: true }))));
    }

    if (isEdit) {
      // -- existing event: four GETs, single skeleton until all resolve --
      const id = s0.meta.eventId;
      (async () => {
        try {
          const typeData = await api.getTypePage(id);
          if (cancelled) return;
          if (!typeData || !typeData.id) throw new Error("not-found");
          const typeState = hydrateType(typeData);
          const kind = typeState.meta.kind;
          const [detailsData, availabilityData, advancedData] = await Promise.all([
            api.getDetailsPage(id, kind === KIND.PAID),
            api.getAvailabilityPage(id),
            api.getAdvancedPage(id),
          ]);
          if (cancelled) return;
          const detailsState = hydrateDetails(detailsData, kind, typeState.type.event_name);
          const availabilityState = hydrateAvailability(availabilityData, { userPlan: redux.userPlan, features: redux.features });
          const advancedState = hydrateAdvanced(advancedData);
          dispatch(
            actions.hydrate({
              meta: { ...typeState.meta, loading: false },
              type: typeState.type,
              details: detailsState.details,
              availability: availabilityState.availability,
              advanced: advancedState.advanced,
              lookups: { ...typeState.lookups, ...availabilityState.lookups, ...advancedState.lookups },
            })
          );
          if (kind === KIND.PAID && featureEnabled(redux.features, FEATURE_IDS.COLLECT_PAYMENTS)) {
            api
              .getPaymentIntegrationStatus()
              .then(safe((paymentConnected) => dispatch(actions.setLookups({ paymentConnected, paymentStatusLoaded: true }))))
              .catch(safe(() => dispatch(actions.setLookups({ paymentConnected: false, paymentStatusLoaded: true }))));
          }
          // keep the URL honest with the stored kind; Payments only exists for paid events
          const q = qs.parse(window.location.search || "");
          const section = stateRef.current.meta.section === SECTIONS.PAYMENTS && kind !== KIND.PAID ? SECTIONS.QUICK : stateRef.current.meta.section;
          if (q.type !== queryFromKind(kind) || section !== stateRef.current.meta.section) {
            dispatch(actions.setMeta({ section }));
            navigate(`${ROUTES.BUILDER}/${id}?type=${queryFromKind(kind)}&section=${section}`, { replace: true });
          }
          // 9.3: best-effort template match → shows the template name in the Quick setup select
          templatesP.then(
            safe((templates) => {
              const eventRow = (availabilityState.availability.schedules || []).find((r) => r.type === SCHEDULE_TYPE.EVENT);
              const def = defaultCompanyTemplate(templates);
              if (!eventRow || !def) return;
              api
                .getScheduleById(def.value)
                .then(
                  safe((data) => {
                    const { rows } = templateToRows(data);
                    if (sameWeeklyRows(rows, eventRow.schedules)) {
                      dispatch(actions.hydrate({ availability: { templateId: String(def.value), scheduleSource: SCHEDULE_SOURCE.TEMPLATE } }));
                    }
                  })
                )
                .catch(() => {});
            })
          );
        } catch (e) {
          if (cancelled) return;
          const status = api.errorStatus(e);
          // the page GETs answer 422 `error_id_required` for an unknown id
          const notFound = status === 404 || status === 422 || (e && e.message === "not-found");
          dispatch(actions.setMeta({ loading: false, loadError: notFound ? "not-found" : "load-failed" }));
        }
      })();
      return () => {
        cancelled = true;
      };
    }

    // -- new event: defaults from lookups (9.2) --
    // Fetch the user's saved timezone from their profile (most reliable source; covers the case
    // where localStorage was not refreshed after the account-setup step).
    if (!isEdit) {
      api.getUserTimezoneId()
        .then(safe((tzId) => {
          if (!tzId) return;
          // Persist to localStorage so guessTimezoneId can use it synchronously on next load
          try { localStorage.setItem("userTimezoneId", tzId); } catch (e) { /* ignore */ }
          const s = stateRef.current;
          // Only apply if the event hasn't already been given a timezone
          if (s.availability.timezone_id && String(s.availability.timezone_id) !== String(tzId)) return;
          const schedules = (s.availability.schedules || []).map((r) =>
            r.type === SCHEDULE_TYPE.EVENT ? { ...r, timezoneId: tzId } : r
          );
          dispatch(actions.hydrate({ availability: { timezone_id: tzId, schedules } }));
        }))
        .catch(() => { /* non-fatal — guessTimezoneId already ran */ });
    }

    connectedVenuesP.then(
      safe((connectedVenues) => {
        const s = stateRef.current;
        if (s.type.venue_id !== "") return; // user already chose
        const first = CONFERENCING_VENUES.find((v) => isVenueConnected(v, connectedVenues));
        const venueId = first || VENUE.PHONE;
        dispatch(actions.hydrate({ type: { venue_id: String(venueId), invitee_call_status: INVITEE_CALL_STATUS.ME } }));
      })
    );
    configP.then(
      safe((config) => {
        const s = stateRef.current;
        const d = parseInt(config && config.event_duration, 10) || 0;
        // old page: seed the duration from event settings unless the user already changed it
        if (d > 0 && s.save.version[STEPS.AVAILABILITY] === 0) {
          dispatch(actions.hydrate({ availability: { event_duration_in_minutes: d } }));
        }
      })
    );
    templatesP.then(
      safe(async (templates) => {
        const def = defaultCompanyTemplate(templates);
        const seed = (rows, timezoneId, templateId) => {
          const s = stateRef.current;
          const hasRows = (s.availability.schedules || []).some((r) => r.type === SCHEDULE_TYPE.EVENT);
          if (hasRows) return; // user already built a schedule
          dispatch(
            actions.hydrate({
              availability: {
                schedules: [{ type: SCHEDULE_TYPE.EVENT, staffId: 0, timezoneId, schedules: rows }, ...(s.availability.schedules || [])],
                timezone_id: timezoneId || s.availability.timezone_id,
                templateId: templateId ? String(templateId) : "",
                scheduleSource: templateId ? SCHEDULE_SOURCE.TEMPLATE : SCHEDULE_SOURCE.CUSTOM,
              },
            })
          );
        };
        const timezones = await timezonesP;
        if (cancelled) return;
        const accountTz = guessTimezoneId(timezones || []);
        if (def) {
          try {
            const data = await api.getScheduleById(def.value);
            if (cancelled) return;
            const { rows, timezoneId } = templateToRows(data);
            seed(rows, accountTz || timezoneId, def.value);
            return;
          } catch (e) {
            /* fall through to Mon–Fri */
          }
        }
        seed(defaultWeeklyRows(), accountTz, null);
      })
    );

    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [ready]);

  // ---- derived: validation issues -------------------------------------------------------------------------------------
  const issuesByStep = useMemo(() => {
    if (!ready || state.meta.loading) return {};
    const redux = { userPlan, features, limits, featuresLoaded: ready, featureAccess };
    return {
      [STEPS.TYPE]: validateStep(STEPS.TYPE, state, redux),
      [STEPS.DETAILS]: validateStep(STEPS.DETAILS, state, redux),
      [STEPS.AVAILABILITY]: validateStep(STEPS.AVAILABILITY, state, redux),
      [STEPS.ADVANCED]: validateStep(STEPS.ADVANCED, state, redux),
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [state.type, state.details, state.availability, state.advanced, state.meta, state.lookups, ready, userPlan, features, limits]);
  const issues = useMemo(() => flattenIssues(issuesByStep), [issuesByStep]);

  // ---- publish / retry ---------------------------------------------------------------------------------------------------
  const publish = useCallback(async () => {
    const result = await engineRef.current.publish();
    if (!result.ok) {
      if (result.issues) {
        const first = flattenIssues(result.issues)[0];
        if (first) focusBlock(first.anchor, first.section);
      } else if (result.error && result.error !== "not-ready" && result.error !== "unavailable") {
        notification.error(result.error);
      }
    }
    return result;
  }, [focusBlock]);

  const retry = useCallback(() => engineRef.current.retry(), []);
  const flush = useCallback(() => engineRef.current.flush(), []);

  const reloadTemplates = useCallback(async () => {
    try {
      const scheduleTemplates = await api.getScheduleTemplateList();
      dispatch(actions.setLookups({ scheduleTemplates, scheduleTemplatesLoaded: true }));
      return scheduleTemplates;
    } catch (e) {
      return stateRef.current.lookups.scheduleTemplates;
    }
  }, []);

  const value = useMemo(
    () => ({
      state,
      dispatch,
      update,
      setKind,
      ensurePaymentStatus,
      goToSection,
      focusBlock,
      toast,
      confirm,
      closeConfirm,
      publish,
      retry,
      flush,
      reloadTemplates,
      issues,
      issuesByStep,
      ready,
      businessId,
      redux: { userPlan, features, limits, featureAccess, smsAllowed: isSmsAllowed(userPlan, featureAccess), canUseStaff: canUseOrganizationStaff(userPlan) },
      hasFeature: (id) => featureEnabled(features, id),
      engine: engineRef.current,
    }),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [state, update, setKind, ensurePaymentStatus, goToSection, focusBlock, toast, confirm, closeConfirm, publish, retry, flush, reloadTemplates, issues, issuesByStep, ready, businessId, userPlan, features, limits, featureAccess]
  );

  return <BuilderContext.Provider value={value}>{children}</BuilderContext.Provider>;
}

export const useBuilder = () => {
  const ctx = useContext(BuilderContext);
  if (!ctx) throw new Error("useBuilder must be used inside BuilderProvider");
  return ctx;
};

/** Weekly rows equality (day set + intervals), ignoring order. */
export function sameWeeklyRows(a, b) {
  const norm = (rows) =>
    (rows || [])
      .filter((r) => r.dayId !== null && r.dayId !== undefined && (r.schedules || []).length)
      .map((r) => `${r.dayId}:${(r.schedules || []).map((s) => `${s.start_time}-${s.end_time}`).sort().join(",")}`)
      .sort()
      .join("|");
  return norm(a) === norm(b) && norm(a) !== "";
}
